import { logger } from '../logger.js';
export class WasmKernelBridge {
    inst = null;
    exports = {};
    mem = null;
    fmem = null;
    _available = false;
    get available() { return this._available; }
    /**
     * Initialize from a base64-encoded wasm binary.
     * This is the preferred path — no fetch, no CORS, works everywhere.
     */
    async initFromBase64(b64) {
        try {
            const bin = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
            return this.initFromBytes(bin);
        }
        catch (e) {
            logger.warn('WasmKernelBridge: base64 decode failed', e);
            return { available: false, binarySize: 0, exports: [] };
        }
    }
    /**
     * Initialize from raw wasm bytes (ArrayBuffer or Uint8Array).
     */
    async initFromBytes(bytes) {
        try {
            const buf = bytes instanceof Uint8Array ? bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) : bytes;
            const mod = await WebAssembly.compile(buf);
            this.inst = await WebAssembly.instantiate(mod);
            this.exports = this.inst.exports;
            const memory = this.exports.memory;
            this.mem = new Uint8Array(memory.buffer);
            this.fmem = new Float32Array(memory.buffer);
            this._available = true;
            // Query __heap_base — standard export from Emscripten/wasm-bindgen/clang.
            // This is the first safe address for user data in linear memory.
            // If the module uses stack locals, writing below __heap_base clobbers them.
            const heapBase = this.exports.__heap_base;
            if (typeof heapBase === 'object' && 'value' in heapBase) {
                this.arenaBase = heapBase.value;
            }
            else if (typeof heapBase === 'number') {
                this.arenaBase = heapBase;
            }
            else {
                this.arenaBase = 0; // backward compat — module has no __heap_base
            }
            this.arenaOffset = this.arenaBase;
            const exportNames = WebAssembly.Module.exports(mod)
                .filter(e => e.kind === 'function')
                .map(e => e.name)
                .filter(n => !n.startsWith('__'));
            return { available: true, binarySize: buf.byteLength, exports: exportNames };
        }
        catch (e) {
            logger.warn('WasmKernelBridge: init failed', e);
            return { available: false, binarySize: 0, exports: [] };
        }
    }
    /**
     * Initialize by fetching a wasm binary from a URL.
     */
    async initFromUrl(url) {
        const resp = await fetch(url);
        const buf = await resp.arrayBuffer();
        return this.initFromBytes(buf);
    }
    /**
     * Ensure wasm linear memory is large enough.
     * Grows if needed — views are refreshed after growth.
     */
    ensureMemory(bytesNeeded) {
        const memory = this.exports.memory;
        const currentSize = memory.buffer.byteLength;
        if (bytesNeeded > currentSize) {
            const pagesNeeded = Math.ceil((bytesNeeded - currentSize) / 65536);
            memory.grow(pagesNeeded);
            this.mem = new Uint8Array(memory.buffer);
            this.fmem = new Float32Array(memory.buffer);
        }
    }
    /**
     * Arena bump allocator for wasm linear memory.
     * Each kernel call resets the arena to 0. Within a single kernel invocation,
     * multiple allocations (data + scratch) are placed sequentially.
     * This prevents concurrent kernel calls from clobbering each other's data
     * when called from the same thread (sequential calls are fine since the
     * arena resets between them).
     *
     * For truly concurrent kernel execution (future plugin architecture with
     * multiple workers), each worker would own its own WasmKernelBridge instance.
     */
    arenaBase = 0;
    arenaOffset = 0;
    arenaReset() { this.arenaOffset = this.arenaBase; }
    arenaAlloc(bytes) {
        // Align to 16 bytes for SIMD compatibility
        const aligned = Math.ceil(this.arenaOffset / 16) * 16;
        this.ensureMemory(aligned + bytes);
        this.arenaOffset = aligned + bytes;
        return aligned;
    }
    /**
     * Call a wasm export function with the given arguments.
     * Low-level — use runKernel() for the standard copy-in/copy-out pattern.
     */
    call(name, ...args) {
        const fn = this.exports[name];
        if (!fn)
            throw new Error(`WasmKernelBridge: export '${name}' not found`);
        return fn(...args);
    }
    /**
     * Run a kernel on pixel data using the standard pattern:
     *   1. Reset arena, allocate space for data
     *   2. Copy data into wasm linear memory
     *   3. Call the kernel export
     *   4. Copy results back
     *
     * @param name — wasm export function name
     * @param data — pixel data (mutated in-place)
     * @param args — additional arguments passed after (dataOffset, dataLength)
     */
    runPixelKernel(name, data, ...args) {
        if (!this._available)
            return;
        const len = data.length;
        this.arenaReset();
        const offset = this.arenaAlloc(len);
        this.mem.set(data, offset);
        this.exports[name](offset, len, ...args);
        data.set(this.mem.subarray(offset, offset + len));
    }
    /**
     * Run a kernel that needs a scratch buffer (e.g. sharpen, blur).
     * Data and scratch are placed sequentially via the arena allocator.
     */
    runPixelKernelWithScratch(name, data, scratchBytes, ...args) {
        if (!this._available)
            return;
        const len = data.length;
        this.arenaReset();
        const dataOffset = this.arenaAlloc(len);
        const scratchOffset = this.arenaAlloc(scratchBytes);
        this.mem.set(data, dataOffset);
        this.exports[name](dataOffset, len, scratchOffset, ...args);
        data.set(this.mem.subarray(dataOffset, dataOffset + len));
    }
    /**
     * Run a named kernel — implements the WasmKernelRunner interface used by KernelRegistry.
     * Maps (name, data, w, h, params) to the wasm export's calling convention.
     *
     * The wasm export receives (dataPtr, dataLen, ...params) where additional params
     * from the params record are passed as trailing arguments in alphabetical key order.
     * w and h are NOT automatically injected — kernels that need them should receive
     * them via the params record.
     *
     * @returns true if the kernel executed, false if unavailable or export not found
     */
    runKernel(name, data, w, h, params) {
        if (!this._available)
            return false;
        const fn = this.exports[name];
        if (!fn)
            return false;
        const len = data.length;
        this.arenaReset();
        const offset = this.arenaAlloc(len);
        this.mem.set(data, offset);
        // Build trailing args from params (sorted by key for deterministic order)
        const extraArgs = params ? Object.keys(params).sort().map(k => params[k]) : [];
        fn(offset, len, ...extraArgs);
        data.set(this.mem.subarray(offset, offset + len));
        return true;
    }
    /**
     * Get a direct view into wasm linear memory.
     * Useful for placing auxiliary data (LUTs, kernels) before calling exports.
     */
    getMemoryView() {
        if (!this.mem || !this.fmem)
            return null;
        return { u8: this.mem, f32: this.fmem };
    }
    /**
     * Check if a specific export exists.
     */
    hasExport(name) {
        return name in this.exports;
    }
    get capabilities() {
        if (!this._available)
            return { available: false, binarySize: 0, exports: [] };
        const memory = this.exports.memory;
        return {
            available: true,
            binarySize: memory.buffer.byteLength,
            exports: Object.keys(this.exports).filter(n => !n.startsWith('__') && n !== 'memory'),
        };
    }
}
//# sourceMappingURL=wasm-kernel-bridge.js.map